import React, { useState, useEffect, useCallback } from 'react';
import { saveMood, getTodayMood } from '../utils/api';
import { toast } from 'sonner';
import { Check } from 'lucide-react';

const MOOD_OPTIONS = [
  { score: 1, label: 'Very low', emoji: '😔' },
  { score: 2, label: 'Low', emoji: '😕' },
  { score: 3, label: 'Okay', emoji: '😐' },
  { score: 4, label: 'Good', emoji: '🙂' },
  { score: 5, label: 'Very good', emoji: '😊' },
];

const MoodCheckIn = ({ variant = 'default', onMoodSaved = null }) => {
  const [selectedMood, setSelectedMood] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasCheckedInToday, setHasCheckedInToday] = useState(false);
  const [loading, setLoading] = useState(true);

  const isDashboard = variant === 'dashboard';
  const isHeader = variant === 'header';

  useEffect(() => {
    const loadTodayMood = async () => {
      try {
        const todayMood = await getTodayMood();
        if (todayMood) {
          setSelectedMood(todayMood.score);
          setHasCheckedInToday(true);
        }
      } catch (error) {
        console.error('Error loading today mood:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTodayMood();
  }, []);

  const handleMoodSelect = useCallback(async (score) => {
    if (saving || hasCheckedInToday) return;
    
    setSaving(true);
    
    try {
      await saveMood(score);
      setSelectedMood(score);
      setHasCheckedInToday(true);
      
      // Show green toast confirmation for header variant
      if (isHeader) {
        toast.success(
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4" />
            <span>Thanks for sharing.</span>
          </div>,
          { duration: 3500 }
        );
      }
      
      if (onMoodSaved) {
        onMoodSaved(score);
      }
    } catch (error) {
      console.error('Error saving mood:', error);
    } finally {
      setSaving(false);
    }
  }, [saving, hasCheckedInToday, onMoodSaved, isHeader]);

  // Header variant - always visible, right-aligned, with toast confirmation
  if (isHeader) {
    if (loading) return null;

    // Show emojis - remain visible even after check-in (but disabled)
    return (
      <div className="flex items-center gap-1.5">
        {MOOD_OPTIONS.map((mood) => {
          const isSelected = hasCheckedInToday && selectedMood === mood.score;
          return (
            <button
              key={mood.score}
              onClick={() => handleMoodSelect(mood.score)}
              disabled={saving || hasCheckedInToday}
              className={`
                w-9 h-9 rounded-full flex items-center justify-center
                bg-slate-50 border border-slate-200
                transition-all duration-150
                ${isSelected ? 'ring-2 ring-primary ring-offset-1 bg-primary/10' : ''}
                ${saving ? 'opacity-50 cursor-not-allowed' : ''}
                ${hasCheckedInToday ? 'cursor-default' : 'cursor-pointer hover:scale-110 hover:bg-slate-100 hover:border-slate-300'}
              `}
              title={mood.label}
              aria-label={mood.label}
            >
              <span className="text-[22px]">{mood.emoji}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // Dashboard variant - centered, silent save
  if (isDashboard) {
    if (loading) return null;

    // Already checked in - hide completely (silent, no reminders)
    if (hasCheckedInToday) return null;

    // Show centered mood emojis - no labels, no messages
    return (
      <div className="flex items-center justify-center gap-2">
        {MOOD_OPTIONS.map((mood) => (
          <button
            key={mood.score}
            onClick={() => handleMoodSelect(mood.score)}
            disabled={saving}
            className={`
              w-9 h-9 rounded-full flex items-center justify-center
              bg-slate-50 hover:bg-slate-100 border border-slate-200
              transition-all duration-150
              ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-110 hover:border-slate-300'}
            `}
            title={mood.label}
            aria-label={mood.label}
          >
            <span className="text-xl">{mood.emoji}</span>
          </button>
        ))}
      </div>
    );
  }

  // Symptoms page variant
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-100 p-3">
        <div className="animate-pulse flex items-center justify-center gap-3">
          <div className="h-3 bg-slate-100 rounded w-32"></div>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="w-8 h-8 bg-slate-100 rounded-full"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Already checked in - subtle note on Symptoms page
  if (hasCheckedInToday) {
    return (
      <div className="bg-slate-50 rounded-xl p-3">
        <p className="text-xs text-slate-400 text-center">
          Check-in saved for today
        </p>
      </div>
    );
  }

  // Show check-in on Symptoms page
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-3">
      <div className="flex items-center justify-center gap-3">
        <span className="text-sm text-slate-500">
          How are you feeling today?
        </span>
        <div className="flex items-center gap-2">
          {MOOD_OPTIONS.map((mood) => (
            <button
              key={mood.score}
              onClick={() => handleMoodSelect(mood.score)}
              disabled={saving}
              className={`
                w-10 h-10 rounded-full flex items-center justify-center
                bg-slate-50 hover:bg-slate-100 border border-slate-200
                transition-all duration-150
                ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-110'}
              `}
              title={mood.label}
              aria-label={mood.label}
            >
              <span className="text-lg">{mood.emoji}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MoodCheckIn;
